import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Globe, Smartphone, TrendingUp, ShoppingBag, 
  CheckCircle2, Sparkles, ArrowRight, ChevronDown, ChevronLeft, ChevronRight, ExternalLink 
} from "lucide-react";
import BannerSlider from "../components/Bannerslidefront";
import { CARDS_DATA } from "./Portfolio";

export default function Home() {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  // FEATURED CLIENT PROJECTS CAROUSEL SLIDER DATA FROM PORTFOLIO PAGE
  const portfolioProjects = CARDS_DATA.slice(0, 12); // First 12 items from Portfolio dataset

  // DYNAMIC RESPONSIVE CARDS PER SLIDE (1 CARD ON MOBILE, 2 ON TABLET, 3 ON DESKTOP)
  const [cardsPerSlide, setCardsPerSlide] = useState(3);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setCardsPerSlide(1); // 1 single card slide on Mobile
      } else if (window.innerWidth < 1024) {
        setCardsPerSlide(2); // 2 cards on Tablet
      } else {
        setCardsPerSlide(3); // 3 cards on Desktop
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const totalSlides = Math.ceil(portfolioProjects.length / cardsPerSlide);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    setCurrentSlide(0);
  }, [cardsPerSlide]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 6000);
    return () => clearInterval(timer);
  }, [totalSlides]);

  const visibleProjects = portfolioProjects.slice(
    currentSlide * cardsPerSlide,
    (currentSlide + 1) * cardsPerSlide
  );

  const stats = [
    { value: "300+", label: "Delivered Client Projects", icon: <CheckCircle2 className="text-blue-600 dark:text-cyan-400" size={20} /> },
    { value: "100%", label: "iOS, Android & Web Support", icon: <Smartphone className="text-indigo-600 dark:text-violet-400" size={20} /> },
    { value: "200+", label: "WordPress, Shopify & React Sites", icon: <Globe className="text-emerald-600 dark:text-emerald-400" size={20} /> },
    { value: "24/7", label: "Dedicated Support Team", icon: <Sparkles className="text-rose-600 dark:text-rose-400" size={20} /> },
  ];

  const services = [
    {
      title: "🌐 Website Development",
      description: "Crafting high-performance, custom websites using cutting-edge architectures like React, Next.js, PHP, WordPress, and Shopify.",
      icon: <Globe size={28} className="text-blue-600 dark:text-cyan-400" />,
      link: "/services"
    },
    {
      title: "📱 App Development",
      description: "Engineering native and cross-platform mobile applications optimized flawlessly for iOS and Android environments.",
      icon: <Smartphone size={28} className="text-indigo-600 dark:text-violet-400" />,
      link: "/services"
    },
    {
      title: "🚀 Digital Marketing & SEO",
      description: "Driving explosive organic and paid growth through technical SEO, performance advertising, and content strategies.",
      icon: <TrendingUp size={28} className="text-emerald-600 dark:text-emerald-400" />,
      link: "/services"
    },
    {
      title: "🛒 E-Commerce Solutions",
      description: "Building scalable online stores with seamless payment gateways, inventory management, and conversion-focused designs.",
      icon: <ShoppingBag size={28} className="text-amber-600 dark:text-amber-400" />,
      link: "/services"
    }
  ];

  const faqs = [
    {
      question: "What types of websites and applications do you build?",
      answer: "We engineer full-stack web solutions including custom React/Next.js platforms, WordPress & Shopify e-commerce portals, PHP backends, and native iOS/Android mobile apps."
    },
    {
      question: "How long does a typical web or mobile app development project take?",
      answer: "Essential websites are delivered in 1-2 weeks. Complex custom web applications and cross-platform mobile apps typically take 3-6 weeks depending on feature requirements."
    },
    {
      question: "Do you offer post-launch maintenance and technical support?",
      answer: "Yes, all our plans include cloud hosting setup, SSL security certificates, and dedicated technical maintenance support packages."
    },
    {
      question: "Can I scale or adjust my pricing plan as my enterprise expands?",
      answer: "Absolutely. Our packages are designed for predictable growth—you can upgrade your tier or add custom feature modules anytime."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#030712] text-slate-900 dark:text-slate-100 antialiased selection:bg-blue-600 selection:text-white transition-colors duration-300 font-sans pt-0">
      
      {/* SECTION 1: HERO FRONT BANNER SLIDER (FLUSH BELOW NAVBAR WITH 0 GAP) */}
      <section className="w-full relative z-10 mt-0 pt-0 px-2 sm:px-4 lg:px-8">
        <BannerSlider />
      </section>

      {/* SECTION 2: STATS & TRUST BAR */}
      <section className="py-12 bg-white dark:bg-slate-900/60 border-y border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {stats.map((stat, idx) => (
              <div key={idx} className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200/80 dark:border-slate-700/50 space-y-2">
                <div className="flex justify-center">{stat.icon}</div>
                <div className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">{stat.value}</div>
                <div className="text-xs font-semibold text-slate-600 dark:text-slate-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3: SERVICES ECOSYSTEM */}
      <section className="py-16 md:py-24 bg-slate-50 dark:bg-[#030712]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-500/15 border border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-blue-400">
              <Sparkles size={13} />
              <span className="text-xs font-bold uppercase tracking-wider">Engineering Capabilities</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              High-Performance <span className="text-blue-600 dark:text-blue-400">Digital Solutions</span>
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-sm md:text-base font-medium max-w-xl mx-auto">
              End-to-end software development and growth strategies tailored for startups, SMBs, and enterprise brands.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((svc, idx) => (
              <div 
                key={idx}
                className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-4 hover:border-blue-500/50 shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col justify-between group"
              >
                <div className="space-y-3">
                  <div className="p-3 w-fit rounded-xl bg-slate-100 dark:bg-slate-800 group-hover:scale-110 transition-transform">
                    {svc.icon}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">{svc.title}</h3>
                  <p className="text-xs text-slate-600 dark:text-slate-400 font-medium leading-relaxed">{svc.description}</p>
                </div>

                <Link 
                  to={svc.link}
                  className="inline-flex items-center gap-2 text-xs font-bold text-blue-600 dark:text-cyan-400 hover:gap-3 transition-all pt-2"
                >
                  <span>Learn More</span>
                  <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4: CLIENT SHOWCASE - FEATURED CLIENT PROJECTS CAROUSEL SLIDER (1 CARD SLIDE ON MOBILE) */}
      <section className="py-16 md:py-24 bg-slate-100/70 dark:bg-slate-900/40 border-y border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div className="space-y-2">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-500/15 border border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-blue-400">
                <Sparkles size={13} />
                <span className="text-xs font-bold uppercase tracking-wider">Client Showcase</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                Featured Client <span className="text-blue-600 dark:text-blue-400">Projects</span>
              </h2>
              <p className="text-slate-600 dark:text-slate-400 text-sm font-medium max-w-lg">
                Explore real-world client deployments across web platforms, e-commerce stores, and mobile applications.
              </p>
            </div>

            <Link to="/Portfolio" className="btn-outline h-10 px-4 text-xs font-bold rounded-xl flex items-center gap-2 w-fit cursor-pointer">
              <span>View All 300+ Projects</span>
              <ArrowRight size={14} />
            </Link>
          </div>

          {/* DYNAMIC RESPONSIVE CAROUSEL SLIDER CONTAINER */}
          <div className="relative max-w-7xl mx-auto px-2">
            
            {/* MIDDLE FLOATING PREV BUTTON */}
            <button
              onClick={prevSlide}
              className="absolute -left-3 sm:-left-5 lg:-left-6 top-1/2 -translate-y-1/2 z-30 w-11 h-11 rounded-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 shadow-xl hover:bg-blue-600 dark:hover:bg-blue-500 hover:text-white hover:border-blue-600 dark:hover:border-blue-400 hover:scale-110 active:scale-95 transition-all duration-200 cursor-pointer flex items-center justify-center group/btn"
              aria-label="Previous Slide"
            >
              <ChevronLeft size={22} className="text-blue-600 dark:text-cyan-400 group-hover/btn:text-white transition-colors" />
            </button>

            {/* MIDDLE FLOATING NEXT BUTTON */}
            <button
              onClick={nextSlide}
              className="absolute -right-3 sm:-right-5 lg:-right-6 top-1/2 -translate-y-1/2 z-30 w-11 h-11 rounded-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 shadow-xl hover:bg-blue-600 dark:hover:bg-blue-500 hover:text-white hover:border-blue-600 dark:hover:border-blue-400 hover:scale-110 active:scale-95 transition-all duration-200 cursor-pointer flex items-center justify-center group/btn"
              aria-label="Next Slide"
            >
              <ChevronRight size={22} className="text-blue-600 dark:text-cyan-400 group-hover/btn:text-white transition-colors" />
            </button>

            <div className="relative min-h-[420px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`${currentSlide}-${cardsPerSlide}`}
                  drag="x"
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.2}
                  onDragEnd={(_, info) => {
                    if (info.offset.x < -50) nextSlide();
                    if (info.offset.x > 50) prevSlide();
                  }}
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -40 }}
                  transition={{ duration: 0.35, ease: "easeInOut" }}
                  className={`grid gap-6 items-stretch cursor-grab active:cursor-grabbing ${
                    cardsPerSlide === 1 
                      ? "grid-cols-1 max-w-md mx-auto" 
                      : cardsPerSlide === 2 
                      ? "grid-cols-2" 
                      : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                  }`}
                >
                  {visibleProjects.map((item) => (
                    <div
                      key={item.id}
                      className="bg-white dark:bg-slate-900/90 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-md hover:shadow-xl hover:border-blue-500/50 transition-all duration-300 flex flex-col justify-between group"
                    >
                      <div>
                        {/* Image Thumbnail with Overlay */}
                        <div className="relative h-48 sm:h-52 w-full overflow-hidden bg-slate-100 dark:bg-slate-800">
                          <img
                            src={item.imageUrl}
                            alt={item.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute top-3 left-3">
                            <span className="px-3 py-1 rounded-full bg-blue-600/90 text-white text-[10px] font-extrabold uppercase tracking-wider shadow-sm">
                              {item.category}
                            </span>
                          </div>
                        </div>

                        {/* Card Content */}
                        <div className="p-5 space-y-3">
                          <h3 className="text-lg font-extrabold text-slate-950 dark:text-white line-clamp-1 group-hover:text-blue-600 dark:group-hover:text-cyan-400 transition-colors">
                            {item.title}
                          </h3>
                          <p className="text-xs text-slate-600 dark:text-slate-400 font-medium leading-relaxed line-clamp-2">
                            {item.descriptions}
                          </p>
                        </div>
                      </div>

                      {/* Footer Action Link */}
                      <div className="p-5 pt-0 mt-auto">
                        {item.url && (
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-primary-blue w-full h-10 px-4 text-xs font-bold rounded-xl flex items-center justify-center gap-2 !text-white cursor-pointer shadow-sm hover:scale-[1.02] transition-transform"
                          >
                            <span>Visit Live Website</span>
                            <ExternalLink size={14} className="!text-white" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* CAROUSEL PAGINATION DOTS */}
            <div className="flex items-center justify-center gap-2 mt-8">
              {Array.from({ length: totalSlides }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentSlide(i)}
                  className={`h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                    currentSlide === i ? "w-8 bg-blue-600 dark:bg-cyan-400" : "w-2.5 bg-slate-300 dark:bg-slate-800"
                  }`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>

          </div>

        </div>
      </section>

      {/* SECTION 5: FREQUENTLY ASKED QUESTIONS */}
      <section className="py-16 md:py-24 bg-slate-50 dark:bg-[#030712]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <div className="text-center space-y-3">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-500/15 border border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-blue-400">
              <Sparkles size={13} />
              <span className="text-xs font-bold uppercase tracking-wider">Got Questions?</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              Frequently Asked <span className="text-blue-600 dark:text-blue-400">Questions</span>
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div 
                key={idx}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-xs transition-colors"
              >
                <button
                  onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
                  className="w-full p-5 text-left flex items-center justify-between gap-4 font-bold text-slate-900 dark:text-white text-sm sm:text-base cursor-pointer"
                >
                  <span>{faq.question}</span>
                  <ChevronDown 
                    size={18} 
                    className={`shrink-0 text-blue-600 dark:text-cyan-400 transition-transform duration-300 ${activeFaq === idx ? "rotate-180" : ""}`}
                  />
                </button>

                <AnimatePresence>
                  {activeFaq === idx && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="px-5 pb-5 text-xs sm:text-sm text-slate-600 dark:text-slate-400 font-medium leading-relaxed border-t border-slate-100 dark:border-slate-800/60 pt-3"
                    >
                      {faq.answer}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6: READY TO SCALE CTA BANNER (PURE WHITE IN LIGHT MODE, DEEP SLATE IN DARK MODE) */}
      <section className="py-16 md:py-24 bg-white dark:bg-[#030712] relative overflow-hidden transition-colors duration-300 border-t border-slate-200 dark:border-slate-800">
        
        {/* LIGHTING GLOW BLOBS */}
        <div className="absolute top-1/2 left-1/4 -translate-y-1/2 -translate-x-1/2 w-96 h-96 bg-blue-400/20 dark:bg-blue-500/30 rounded-full blur-[110px] pointer-events-none" />
        <div className="absolute top-1/2 right-1/4 -translate-y-1/2 translate-x-1/2 w-96 h-96 bg-cyan-400/20 dark:bg-violet-600/30 rounded-full blur-[110px] pointer-events-none" />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center space-y-8">
          
          <div className="p-8 sm:p-12 md:p-16 rounded-3xl bg-white dark:bg-slate-900/90 border-2 border-blue-500/30 dark:border-slate-800 shadow-2xl shadow-blue-500/10 space-y-6 text-slate-900 dark:text-white">
            
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-500/20 border border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-cyan-300 shadow-xs">
              <Sparkles size={14} className="text-blue-600 dark:text-cyan-300" />
              <span className="text-xs font-extrabold uppercase tracking-widest text-blue-700 dark:text-cyan-300">Start Your Project Today</span>
            </div>

            <h2 className="text-2xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white tracking-tight leading-tight">
              Ready to Scale Your <span className="text-blue-600 dark:bg-gradient-to-r dark:from-cyan-300 dark:via-teal-200 dark:to-blue-200 dark:bg-clip-text dark:text-transparent">Business Online?</span>
            </h2>

            <p className="text-slate-600 dark:text-slate-300 text-xs sm:text-base font-semibold max-w-2xl mx-auto leading-relaxed">
              Get in touch with our engineering and growth team for high-performance websites, custom apps, and digital marketing strategies.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <Link 
                to="/enquirynow"
                className="w-full sm:w-auto h-12 px-8 text-sm font-extrabold rounded-xl flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 !text-white cursor-pointer shadow-lg shadow-blue-600/30 transition-all hover:scale-105"
              >
                <Sparkles size={16} className="!text-white" />
                <span className="!text-white font-extrabold">Get Custom Proposal</span>
                <ArrowRight size={16} className="!text-white" />
              </Link>
              
              <Link 
                to="/Meeting" 
                className="w-full sm:w-auto h-12 px-6 text-sm font-extrabold rounded-xl flex items-center justify-center gap-2 cursor-pointer bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-900 dark:text-white border border-slate-300 dark:border-slate-700 transition-all"
              >
                <span className="text-slate-900 dark:text-white font-extrabold">Schedule Consultation</span>
              </Link>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}
